import Check from "@mui/icons-material/Check";
import { Box, Button, Typography, useTheme } from "@mui/material";
import { GridRowParams } from "@mui/x-data-grid";
import { ArrowRightIcon } from "@mui/x-date-pickers";
import { OmnisearchDataGrid } from "components/OmnisearchDataGrid";
import { RegistrationDetailDrawer } from "components/waste/registration/RegistrationDetailDrawer";
import { FeatureFlagKey } from "encamp-shared/src/featureFlags";
import { hasValue } from "encamp-shared/src/utils/hasValue";
import { gql } from "generated-graphql";
import {
  WasteClassification,
  WasteStreamRegistrationStatus,
  WasteStreamsQuery,
  WasteStreamsQueryVariables,
} from "generated-graphql/graphql";
import { useCurrentUser } from "hooks/useCurrentUser";
import { useFeatureFlags } from "hooks/useFeatureFlags";
import { OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";
import { useOmnisearchInput } from "hooks/useOmnisearchDatagrid/useOmnisearchInput";
import { useSelectedIdInUrl } from "hooks/useSelectedIdInUrl";
import { useTenant } from "hooks/useTenant";
import { useShowWasteStateRegistration } from "hooks/useWasteStateRegistration";
import { useMemo, useState } from "react";
import { StateRegistrationDialog } from "routes/Customer/Waste/StateRegistrationDialog/StateRegistrationDialog";

import { buildWasteStreamCodeColumns } from "../../../wasteStreamCodeColumns";
import {
  DISCREPANCY_TYPE_FILTER_COLUMN,
  buildConsolidatedIssueLabels,
  filterOpenDiscrepancies,
} from "../../../wasteStreamDiscrepancyIndicator";
import { WasteStreamIssuesCell } from "../../../WasteStreamIssuesCell";
import {
  WasteStreamRegistrationCell,
  WasteStreamStateIdCell,
} from "../../../WasteStreamRegistrationCell";
import { profileNumberValueGetter } from "../formatWasteStreamProfileNumber";
import { MergedWasteStreamChip } from "../MergedWasteStreamChip";
import { prettyPrintWasteClassification } from "../prettyPrintWasteClassification";
import { ProvisionalWasteStreamChip } from "../ProvisionalWasteStreamChip";
import { useWasteFacility } from "../useWasteFacility";
import { useWasteFacilityState } from "../WasteFacilityContext";
import { WasteStreamDetailsModal } from "../WasteStreamDetails/WasteStreamDetailsModal";
import { useWasteStreamSelection } from "./useWasteStreamSelection";
import { WasteStreamExportDropdown } from "./WasteStreamExportDropdown";
import { WasteStreamMergeDialog } from "./WasteStreamMerge/WasteStreamMergeDialog";

export const WASTE_STREAMS = gql(`
  query WasteStreams(
    $facilityId: ID!,
    $search: String
    $page: Int
    $pageSize: Int
    $sort: [SortModel!]
    $startDate: Date
    $endDate: Date
    $view: FacilityWasteView
    $tags: [String!]
    $needsStateRegistration: Boolean
  ) {
    facility(id: $facilityId) {
      state
      wasteStreams(search: $search, page: $page, pageSize: $pageSize, sort: $sort, startDate: $startDate, endDate: $endDate, view: $view, tags: $tags, needsStateRegistration: $needsStateRegistration) {
        items {
          id
          name
          facilityId
          classification
          federalWasteCodes
          stateWasteCodes
          eManifestProfileNumber
          vendorProfileNumber
          formCode
          sourceCode
          managementMethodCodes
          vendorAmount
          eManifestAmount
          documentAmount
          effectiveAmount
          vendorAmountAllTime
          eManifestAmountAllTime
          mergeCount
          comment
          willNotReport
          stateRegisteredId
          stateRegistrationStatus
          stateRegisteredAt
          needsSubmission
          isProvisional
          registrationId
          registrationLinkSource
          registrationDisplayStatus
          registration {
            id
            stateRegisteredId
            status
            name
          }
          openRegistrationSubmission {
            id
          }
          discrepancyCount
          discrepancies {
            id
            field
            kind
            ruleKey
            status
            wasteLineUnifiedId
            eManifestValue
            vendorValue
            manifestDocumentValue
          }
        }
        count
      }
    }
  }
`);

type WasteStreamTileRow = NonNullable<
  NonNullable<WasteStreamsQuery["facility"]>["wasteStreams"]
>["items"][number];

// Give each column a fixed initial width derived from its old `flex` weight so
// the grid overflows and scrolls horizontally instead of squishing columns to
// fit. minWidth stays low (the MUI default) so columns remain resizable to well
// under the initial width.
const COLUMN_WIDTH_PER_FLEX = 280;
const MIN_RESIZE_WIDTH = 50;

// The shared DataGrid shows a pointer cursor on row hover; with row click
// disabled in readOnly mode that would falsely signal clickability, so put the
// default cursor back. Module-level constant so `gridSx` identity is stable.
const READ_ONLY_GRID_SX = {
  "& .MuiDataGrid-row:hover": { cursor: "default" },
};

interface WasteStreamsDataGridProps {
  facilityId: string;
  /**
   * Strips every edit affordance — row-click details modal (and its staff edit
   * dialog), staff merge selection/button, and the registration-submission cell
   * actions — leaving a review-only grid. Search, sort, filters, and export
   * remain. Used by the waste submission stepper, where edits must happen on the
   * waste facility page instead of inline.
   */
  readOnly?: boolean;
  /**
   * Size the grid to its rows rather than filling (and reserving) a fixed
   * height. Used by the stepper's Waste Activity step so the step's footer sits
   * directly under the grid instead of below a tall reserved area.
   */
  fitContent?: boolean;
}

export const WasteStreamsDataGrid = ({
  facilityId,
  readOnly,
  fitContent,
}: WasteStreamsDataGridProps) => {
  const theme = useTheme();
  const { isStaff } = useCurrentUser();
  const { featureFlags } = useFeatureFlags();
  // Field-discrepancy warnings on the waste-stream grid follow the same gate as
  // the manifest discrepancy circles: GA to all waste-module tenants, visible to
  // staff AND customers (no longer staff-only). Other staff-only controls on
  // this grid stay gated on `isStaff`.
  const showDiscrepancies = !!featureFlags[FeatureFlagKey.WasteModule];
  const { tenantId } = useTenant();
  const { data } = useWasteFacility(facilityId);
  const { selectedRows, setSelectedRows, isRowSelectable, canMergeSelected } =
    useWasteStreamSelection<WasteStreamTileRow>();

  const { selectedId, pushIdToUrl, popIdFromUrl } = useSelectedIdInUrl("wasteStreamId", {
    prefixSegment: "wasteStream",
  });
  const {
    selectedId: selectedRegistrationId,
    pushIdToUrl: pushRegistrationIdToUrl,
    popIdFromUrl: popRegistrationIdFromUrl,
  } = useSelectedIdInUrl("registrationId", { prefixSegment: "registration" });

  const handleRowClick = (params: GridRowParams<WasteStreamTileRow>) => {
    pushIdToUrl(params.row.id);
  };

  const {
    wasteFacilityState: { deferredFilters },
  } = useWasteFacilityState();
  const { omnisearch } = useOmnisearchInput({ isURLDriven: true });
  const [isMergeDialogOpen, setIsMergeDialogOpen] = useState(false);
  const [stateRegDialogStream, setStateRegDialogStream] = useState<WasteStreamTileRow | null>(null);

  const facilityState = data?.facility?.state ?? null;
  const showRegistrationColumn = useShowWasteStateRegistration(facilityState);

  const exportFilename = useMemo(
    () => (data.facility?.name ? `${data.facility.name}-waste-streams` : undefined),
    [data.facility?.name]
  );

  const columns: OmnisearchGridColDef<WasteStreamTileRow>[] = useMemo(() => {
    const base: OmnisearchGridColDef<WasteStreamTileRow>[] = [
      {
        field: "name",
        headerName: "Waste Stream",
        flex: 1,
        minWidth: 100,
        sortable: true,
        headerClassName: "wrapHeader",
        renderCell: (params) => (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
              gap: 1,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                flex: 1,
                minWidth: 0,
              }}
            >
              <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minWidth: 0 }}>
                <Typography
                  variant="body2"
                  title={params.value}
                  sx={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {params.value}
                </Typography>
                {params.row.willNotReport && (
                  <Typography variant="caption" color="text.secondary">
                    Not Reportable
                  </Typography>
                )}
              </Box>
              {isStaff && <MergedWasteStreamChip mergeCount={params.row.mergeCount} />}
              <ProvisionalWasteStreamChip isProvisional={params.row.isProvisional} />
            </Box>
            {!readOnly && <ArrowRightIcon sx={{ flexShrink: 0, fontSize: 16 }} />}
          </Box>
        ),
      },
      {
        field: "profileNumber",
        headerName: "Profile Number",
        flex: 0.5,
        sortable: true,
        valueGetter: profileNumberValueGetter,
      },
      ...buildWasteStreamCodeColumns<WasteStreamTileRow>(showDiscrepancies, isStaff),
      {
        field: "willNotReport",
        headerName: "Not Reportable",
        filterKeyType: "boolean",
        sortable: false,
        flex: 0.4,
        headerClassName: "wrapHeader",
        renderCell: ({ value }) => (value === true ? <Check /> : null),
      },
    ];
    if (showRegistrationColumn) {
      base.splice(
        2,
        0,
        {
          // The state-assigned registration id, split out of the Registration
          // cell into its own column. Display-only (no server sort/filter
          // wired for it); the state-registered-ID omnisearch term still
          // covers search.
          field: "stateRegisteredId",
          headerName: "State ID",
          sortable: false,
          filterable: false,
          flex: 0.4,
          headerClassName: "wrapHeader",
          renderCell: ({ row }) => (
            <WasteStreamStateIdCell
              displayStatus={row.registrationDisplayStatus}
              registration={row.registration}
            />
          ),
        },
        {
          // FK-backed registration column: status chip, the "+ State
          // Registration" matching dialog, the in-progress submission link,
          // and the detail-drawer deep link. Non-staff see the status chip
          // only, since the underlying queries/mutations are staff-only;
          // readOnly (the submission stepper's review embed) suppresses every
          // action regardless of staff.
          field: "registrationDisplayStatus",
          headerName: "Registration",
          // Not sortable: the status is derived in a page-scoped CTE, after
          // ORDER BY / LIMIT have already chosen the page (see
          // paged_needs_submission in wasteStreamDataAccess) — computing it
          // across the whole filtered set so it could be sorted is the join
          // that regresses large tenants. A sortable header here would bind to
          // nothing and silently fall back to the default name sort.
          sortable: false,
          filterable: false,
          flex: 0.8,
          headerClassName: "wrapHeader",
          renderCell: ({ row }) => (
            <WasteStreamRegistrationCell
              displayStatus={row.registrationDisplayStatus}
              registration={row.registration}
              openSubmissionId={row.openRegistrationSubmission?.id}
              onOpenRegistration={(registrationId) => {
                if (readOnly) return;
                pushRegistrationIdToUrl(registrationId);
              }}
              onOpenDialog={() => {
                if (readOnly) return;
                setStateRegDialogStream(row);
              }}
              onOpenSubmission={
                readOnly
                  ? undefined
                  : (submissionId) =>
                      window.open(
                        `/o/${tenantId}/waste/submissions/${submissionId}/edit`,
                        "_blank",
                        "noopener,noreferrer"
                      )
              }
            />
          ),
        }
      );
    }
    // Consolidated, staff-only "Issues" column: every open field discrepancy +
    // field issue on the row in one sortable place (server-side sort by open
    // discrepancy count, matching this filter). The per-cell indicators above
    // stay. Gated the same as them (flag) plus isStaff, since the discrepancy
    // data is staff-only. Filtered to open (PENDING/UNRESOLVED) rows —
    // RESOLVED/IGNORED are already done and shouldn't read as outstanding work.
    if (showDiscrepancies && isStaff) {
      base.push({
        field: "discrepancyCount",
        headerName: "Issues",
        flex: 0.9,
        minWidth: 160,
        sortable: true,
        filterable: false,
        headerClassName: "wrapHeader",
        valueGetter: (_, row) =>
          buildConsolidatedIssueLabels(filterOpenDiscrepancies(row.discrepancies, isStaff)).join(
            "; "
          ),
        renderCell: ({ row }) => (
          <WasteStreamIssuesCell
            discrepancies={filterOpenDiscrepancies(row.discrepancies, isStaff)}
          />
        ),
      });
    }
    return base.map(({ flex, ...rest }) => ({
      ...rest,
      width: rest.width ?? Math.round((flex ?? 1) * COLUMN_WIDTH_PER_FLEX),
      minWidth: MIN_RESIZE_WIDTH,
    }));
  }, [
    isStaff,
    showDiscrepancies,
    readOnly,
    showRegistrationColumn,
    pushRegistrationIdToUrl,
    tenantId,
  ]);

  return (
    <>
      <OmnisearchDataGrid<WasteStreamsQuery, WasteStreamsQueryVariables, WasteStreamTileRow>
        persistenceKey="waste-streams-table"
        isURLDriven={true}
        autoHeight={fitContent}
        searchFieldSx={{ "& .MuiOutlinedInput-root": { backgroundColor: "background.paper" } }}
        columns={columns}
        excludeFilterColumns={["name"]}
        dataQuery={WASTE_STREAMS}
        getItems={(data) => data.facility?.wasteStreams?.items ?? []}
        getCount={(data) => data.facility?.wasteStreams?.count ?? 0}
        dataQueryVariables={{
          facilityId,
          startDate: deferredFilters.startDate,
          endDate: deferredFilters.endDate,
          view: deferredFilters.view,
          tags: deferredFilters.tags,
        }}
        initialPageSize={10}
        withPadding={false}
        defaultSearch=""
        noDataMessage={"No Waste Streams found for this facility."}
        initialSortModel={[{ field: "name", sort: "asc" }]}
        onRowClick={readOnly ? undefined : handleRowClick}
        gridSx={readOnly ? READ_ONLY_GRID_SX : undefined}
        disableRowSelectionOnClick={true}
        commandButtons={[
          isStaff && !readOnly ? (
            <Button
              key="merge-waste-streams-button"
              size="small"
              variant="contained"
              sx={{
                color: theme.palette.primary.contrastText,
                backgroundColor: theme.palette.warning.light,
              }}
              endIcon={<ArrowRightIcon />}
              disabled={!canMergeSelected}
              title={`Merge selected waste streams`}
              onClick={() => setIsMergeDialogOpen(true)}
            >
              Merge Waste Streams
            </Button>
          ) : null,
          <WasteStreamExportDropdown
            key="export-waste-streams-button"
            isStaff={isStaff}
            disabled={!facilityId}
            facilityId={facilityId}
            tenantId={tenantId ?? ""}
            fileName={exportFilename}
            filter={{
              search: omnisearch,
              startDate: deferredFilters.startDate,
              endDate: deferredFilters.endDate,
              view: deferredFilters.view,
              tags: deferredFilters.tags,
            }}
            dataQuery={WASTE_STREAMS}
            dataQueryVariables={{
              facilityId,
              startDate: deferredFilters.startDate,
              endDate: deferredFilters.endDate,
              view: deferredFilters.view,
              tags: deferredFilters.tags,
              search: omnisearch || undefined,
            }}
            onWasteStreamClick={pushIdToUrl}
          />,
        ]}
        selectedIds={selectedRows.map((row) => row.id)}
        onSelectedRowsChanged={isStaff && !readOnly ? setSelectedRows : undefined}
        isRowSelectable={isRowSelectable}
        checkboxSelection={isStaff && !readOnly}
        disableSelectAll
        additionalFilterColumns={[
          {
            filterKeyType: "enumAutocomplete",
            key: "classification",
            header: "Classification",
            enumValues: Object.values(WasteClassification),
            enumPresentationFunction: (value) => prettyPrintWasteClassification(value),
          },
          {
            filterKeyType: "boolean",
            key: "isProvisional",
            header: "Provisional",
          },
          ...(showRegistrationColumn
            ? [
                {
                  filterKeyType: "boolean" as const,
                  key: "needsStateRegistration",
                  header: "Needs State Registration",
                },
                {
                  filterKeyType: "enumAutocomplete" as const,
                  key: "stateRegistrationStatus",
                  header: "Registration Status",
                  enumValues: Object.values(WasteStreamRegistrationStatus),
                  ignoreStateAbbreviations: true,
                },
                // Keep the state-registered-ID search after merging the column
                // into "Registration" (backend contains-match, ENG-15798).
                {
                  key: "stateRegisteredId",
                  header: "State Registered ID",
                },
              ]
            : []),
          ...(isStaff ? [DISCREPANCY_TYPE_FILTER_COLUMN] : []),
        ]}
      />

      {isMergeDialogOpen && (
        <WasteStreamMergeDialog
          onClose={() => {
            setIsMergeDialogOpen(false);
            setSelectedRows([]); // Clear selections when dialog closes
          }}
          selectedRows={selectedRows}
        />
      )}

      {!readOnly && stateRegDialogStream && (
        <StateRegistrationDialog
          open
          onClose={() => setStateRegDialogStream(null)}
          facilityId={facilityId}
          facilityName={data.facility?.name ?? "—"}
          state={facilityState ?? ""}
          wasteStream={stateRegDialogStream}
        />
      )}

      {/* readOnly also guards deep links: `wasteStreamId` / `registrationId` in
          the URL would otherwise open the modal / drawer (and staff edit UI)
          without a click. */}
      {!readOnly && selectedId && (
        <WasteStreamDetailsModal
          open={!!selectedId}
          onClose={() => popIdFromUrl()}
          wasteStreamId={selectedId}
          buildLineItemHref={(lineItemId) => `../manifest-line-items/${lineItemId}`}
        />
      )}
      {/* Staff-gated as well as readOnly-gated: the drawer's query is
          staff-only, so a non-staff user landing on a `registration/:id` URL
          would open a drawer that can only ever error. */}
      {!readOnly && isStaff && hasValue(selectedRegistrationId) && (
        <RegistrationDetailDrawer
          registrationId={selectedRegistrationId}
          onClose={popRegistrationIdFromUrl}
          refetchQueries={["WasteStreams"]}
        />
      )}
    </>
  );
};
